import { create } from 'zustand';
import apiClient from '@/services/apiClient';
import {
  CLINIC_SHAPES,
  shapeRank,
  type ClinicShape,
} from '@/config/clinicShape';

/**
 * Fetches and remembers the clinic's derived size. The comparison helpers and
 * the reasoning behind them live in @/config/clinicShape, which stays free of
 * dependencies so the nav config can import it.
 */

interface ClinicShapeState {
  shape: ClinicShape;
  members: number;
  providers: number;
  branches: number;
  /** True once the server has answered; before that we render the widest nav. */
  loaded: boolean;
  /** Owner escape hatch: show every row regardless of size. */
  showEverything: boolean;
  refresh: (tenantId: string | null) => Promise<void>;
  setShowEverything: (on: boolean) => void;
}

const HIGH_WATER_KEY = (tenantId: string) => `maie-clinic-shape:${tenantId}`;
const SHOW_ALL_KEY = 'maie-clinic-shape-show-all';

const readHighWater = (tenantId: string | null): ClinicShape | null => {
  if (!tenantId) return null;
  try {
    const raw = localStorage.getItem(HIGH_WATER_KEY(tenantId));
    return raw && (CLINIC_SHAPES as string[]).includes(raw) ? (raw as ClinicShape) : null;
  } catch {
    return null;
  }
};

const writeHighWater = (tenantId: string | null, shape: ClinicShape) => {
  if (!tenantId) return;
  try {
    localStorage.setItem(HIGH_WATER_KEY(tenantId), shape);
  } catch {
    /* private mode, quota — the nav just re-derives next load */
  }
};

export const useClinicShapeStore = create<ClinicShapeState>((set) => ({
  // Widest shape until proven otherwise: a nav that starts small and grows on
  // load makes rows appear under the cursor. Starting wide and settling down
  // is the less jarring direction, and matches "hide only".
  shape: 'group',
  members: 1,
  providers: 1,
  branches: 1,
  loaded: false,
  showEverything: (() => {
    try {
      return localStorage.getItem(SHOW_ALL_KEY) === '1';
    } catch {
      return false;
    }
  })(),

  refresh: async (tenantId) => {
    try {
      const { data: payload } = await apiClient.get('/me/clinic-shape');
      // The backend wraps successful responses in { success, data }.
      const data = payload?.data ?? payload;
      const served: ClinicShape = data?.shape ?? 'solo';
      const previous = readHighWater(tenantId);
      const shape =
        previous && shapeRank(previous) > shapeRank(served) ? previous : served;
      writeHighWater(tenantId, shape);
      set({
        shape,
        members: data?.members ?? 1,
        providers: data?.providers ?? 1,
        branches: data?.branches ?? 1,
        loaded: true,
      });
    } catch {
      // Never let this narrow the nav. An unreachable endpoint leaves the
      // widest shape in place, which is the same thing every clinic sees today.
      set({ loaded: false });
    }
  },

  setShowEverything: (on) => {
    try {
      localStorage.setItem(SHOW_ALL_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
    set({ showEverything: on });
  },
}));
